<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
        <div class="col-md-12">
          <h3>
            <i class="fa fa-fw fa-qrcode"></i> {{ _('Code checker') }}
          </h3>
          <p>{{ _('Scan any Johku QR code while this view is open to view its details instead of performing the default action.') }}</p>
          <p>{{ _('You can also manually enter a value card code to the input field.') }}</p>
          <div class="form-inline flex-row">
            <input class="form-control" type="text" v-model="code" /><button class="btn btn-default" type="button" v-on:click="checkCode">{{ _('Check') }}</button>
          </div>
        </div>
      </div>
      <div class="row" v-if="ticket && show">
        <div class="col-sm-12">
          <h3><i class="fa fa-fw fa-ticket"></i> {{ _('Ticket') }}</h3>
          <h4>{{ show.product.name }}</h4>
          <h4>{{ DateTime.fromSeconds(Number(show.start_ts), { zone: timezone }).toFormat(dateFormat + ' ' + timeFormat) }} {{ show.resource.name }}</h4>
          <div class="alert alert-success">
            <p><strong>{{ _('Seats') }}</strong></p>
            <div class="seat-items">
              <span class="seat-item" v-for="seat in ticket.ownSeat_ticket">
                {{ getSeatDescription(seat.seat) }}
              </span>
            </div>
          </div>
          <div class="ticketDetails">
            <span v-if="ticket.order_id">{{_('Order')}}: <strong>{{ ticket.order_id}}</strong></span>
            <span v-if="ticket.bill_id">{{_('Transaction')}}: <strong>{{ ticket.bill_id}}</strong></span>
          </div>
        </div>
      </div>
      <div class="row" v-if="valuecard">
        <div class="col-sm-6">
          <h3><i class="fa fa-fw fa-credit-card-alt"></i> {{ _('Value card') }}</h3>
          <div class="alert alert-danger alert-deleted" v-if="parseInt(valuecard.deleted)"><h4 v-if="order && parseInt(order.refunded)">{{ _('Refunded') }}</h4><h4 v-else>{{ _('Deleted') }}</h4></div>
          <p>{{ DateTime.fromSeconds(Number(valuecard.created), { zone: timezone }).toFormat(dateFormat + ' ' + timeFormat) }}</p>
          <h3><span v-if="valuecard.shopTitle">{{ valuecard.shopTitle }}: </span>{{ valuecard.name }}</h3>
          <h3 v-if="!parseInt(valuecard.status)"><span class="text-danger">{{ _('Inactive') }}</span></h3>
          <p v-if="valuecard.code">{{ $filters.addDashes(valuecard.code) }}</p>
          <h4 v-if="parseInt(valuecard.count, 10) > 1">{{ _('Original uses') }}: {{ valuecard.count }}. {{ _('Remaining uses') }}: {{ valuecard.remainingcount }}</h4>
          <template v-if="isStandardValuecard">
            <h4>
              <template v-if="!valuecard.singleuse && !valuecard.singleorder">{{ _('Original value') }}</template><template v-else>{{ _('Value') }}</template> {{ $filters.currency(valuecard.value) }}
              <template v-if="parseInt(valuecard.count, 10) === 1">{{ _('Remaining value') }}: {{ $filters.currency(valuecard.remainingvalue) }}</template>
            </h4>
          </template>
          <template v-else>
            <h4>{{ _('Original uses') }}: {{ valuecard.count }}. {{ _('Remaining uses') }}: {{ valuecard.remainingcount }}</h4>
          </template>
          <hr v-if="!parseInt(valuecard.deleted)">
          <ul v-if="!parseInt(valuecard.deleted)">
            <li v-if="parseInt(valuecard.singleorder, 10) && parseInt(valuecard.count,10) === 1">{{ _('This value card will be completely used on next order.') }}</li>
            <li v-else-if="parseInt(valuecard.singleorder, 10) && parseInt(valuecard.remainingcount,10) > 0 && parseInt(valuecard.count,10) > 1">
              {{ _('This code contains multiple value cards. One of those value cards will be completely used on next order.') }}
            </li>
            <li v-else-if="parseInt(valuecard.singleorder, 10) && parseInt(valuecard.remainingcount,10) === 0 && parseInt(valuecard.count,10) > 1">
              {{ _('This code contains multiple value cards. Last of those value cards will be completely used on next order.') }}
            </li>
            <li v-if="parseInt(valuecard.singleuse, 10)">{{ _('This value card is applied to a single basket item.') }}</li>
            <li v-if="!parseInt(valuecard.singleorder,10) && !parseInt(valuecard.singleuse, 10)">{{ _('This value card can be applied to additional orders in the future.') }}</li>
            <li v-if="parseInt(valuecard.validto, 10)">{{ _('Valid to') }}: {{ $filters.timestampToDate(valuecard.validto, locale.split('_')[0]) }}</li>
            <li v-else>{{ _('No set expiration date.') }}</li>
          </ul>
          <button type="button" class="btn btn-info" v-on:click="setValidityDialog" v-if="!parseInt(valuecard.deleted)"><i class="fa fa-fw fa-calendar-o"></i> {{ _('Set validity') }}</button>
        </div>
        <div class="col-sm-6">
          <template v-if="valuecardHasPayments && valuecard.ownPayment && valuecard.ownPayment.length">
            <h4>{{ _('Usage') }}</h4>
            <table width="100%" class="table">
              <thead>
                <tr>
                  <th>{{ _('Date') }}</th>
                  <th>{{ _('Transaction') }}</th>
                  <th style="text-align: right">{{ _('Sum') }}</th>
                </tr>
              </thead>
              <tbody id="paymentsContainer">
                <template v-for="payment in valuecard.ownPayment">
                  <tr class="payment-row">
                    <td>{{ $filters.timestampToDate(payment.paymentdate) }}</td>
                    <td><button class="btn btn-info btn-xs" type="button" v-on:click="displayInvoice({ id: payment.bill_id })"><i class="fa fa-fw fa-file"></i> {{ payment.bill_id }}</button></td>
                    <td align="right">{{ $filters.currency(payment.amount) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
          <template v-if="valuecardHasPayments && valuecard.ownExternalpayment && valuecard.ownExternalpayment.length">
            <h4>{{ _('External usage') }}</h4>
            <table width="100%" class="table">
              <thead>
                <tr>
                  <th>{{ _('Date') }}</th>
                  <th>{{ _('Shop ID') }}</th>
                  <th style="text-align: right">{{ _('Sum') }}</th>
                </tr>
              </thead>
              <tbody id="externalPaymentsContainer">
                <template v-for="payment in valuecard.ownExternalpayment">
                  <tr class="payment-row">
                    <td>{{ $filters.timestampToDate(payment.paymentdate) }}</td>
                    <td>{{ payment.shopid }}</td>
                    <td align="right">{{ $filters.currency(payment.amount) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
          <!-- Valuecard in baskets -->
          <template v-if="isValuecardInBaskets">
            <h4>{{ _('Baskets') }}</h4>
            <table width="100%" class="table">
              <thead>
                <tr>
                  <th>{{ _('Items in basket') }}</th>
                  <th>{{ _('Last action') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="basketItem in valuecard.ownItem">
                  <tr>
                    <td>{{ basketItem.basket_count }}</td>
                    <td>{{ $filters.timestampToDate(basketItem.basket_lastaction) }} {{ $filters.timestampToTime(basketItem.basket_lastaction) }}</td>
                    <td align="right"><button class="btn btn-danger btn-xs" type="button" v-on:click="deleteItemFromBasket(basketItem.id, basketItem.basket_identifier)">{{ _('Delete') }}</button></td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </div>
      </div>
      <div class="row" v-if="reservation">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-calendar"></i> {{ _('Reservation') }}: {{ reservation.product_name }}</h3>
          <p>{{ DateTime.fromSeconds(Number(reservation.start_ts), { zone: timezone }).toFormat(dateFormat + ' ' + timeFormat) }} {{ _('at') }} {{ DateTime.fromSeconds(Number(reservation.start_ts), { zone: timezone }).toFormat(timeFormat) }} - {{ DateTime.fromSeconds(Number(reservation.end_ts), { zone: timezone }).toFormat(dateFormat + ' ' + timeFormat) }} {{ _('at') }} {{ DateTime.fromSeconds(Number(reservation.end_ts), { zone: timezone }).toFormat(timeFormat) }} </p>
          <template v-if="reservation.sharedResource">
          <template v-if="reservation.sharedResource.length == 1">
          <h4>{{ _('Reserved resource') }}</h4>
          <template v-for="(resource, index) in reservation.sharedResource">
          <p>{{resource.name}}</p>
          </template>
          </template>
          <template v-else>
          <h4>{{ _('Reserved resources') }}</h4>
          <ul>
          <li v-for="(resource, index) in reservation.sharedResource">{{resource.name}}</li>
          </ul>
          </template>
          </template>
          <h4>{{ _('Reserved by') }}</h4>
          <p><strong>{{ reservation.client_name }}</strong><br />
          <span v-if="reservation.client_company">{{ reservation.client_company }}<br /></span>
          {{ reservation.client_phone }}<br />
          {{ reservation.client_email }}</p>
          <div style="margin-top: 10px">
            <button class="btn btn-info" type="button" v-on:click="displayReservation(reservation)"><i class="fa fa-fw fa-calendar"></i> {{ _('Show more') }}</button>
          </div>
        </div>
      </div>
      <div class="row" v-if="order">
        <div class="col-md-12">
          <div class="openlist-transaction">
            <h3><i class="fa fa-fw fa-shopping-cart"></i> {{ _('Order') }} {{ order.id }}</h3>
            <div class="alert alert-danger alert-deleted" v-if="parseInt(order.refunded)"><h4>{{ _('Refunded') }}</h4></div>
            <div>{{ orderFullDate }}</div>
            <div v-if="order.name">{{ order.name }}</div>
            <div v-if="order.name">{{ order.company }}</div>
            <div v-if="order.name">{{ order.ident }}</div>
            <div v-if="order.name">{{ order.address }}</div>
            <div v-if="order.name">{{ order.zip }}</div>
            <div v-if="order.name">{{ order.city }}</div>
            <div v-if="order.name">{{ order.country }}</div>
            <div v-if="order.name">{{ order.phone }}</div>
            <div v-if="order.name">{{ order.email }}</div>
            <div v-else>{{ _('Johku App') }}</div>
            <h4>{{ $filters.currency(order.totalWithDelivery, settings.currency) }}</h4>
            <hr v-if="order.notes" />
            <div v-if="order.notes">{{ order.notes }}</div>
          </div>
          <div v-show="items" class="receipt-items">
            <cart-item parent="openlist" :readonly="true" :settings="shopSettings(shopId)" :shopId="shopId" :baseDomain="baseDomain" :locale="locale" :currItem="item" v-for="item in items.filter(i => !i.discount_id)" :key="item.id" leftcolwidth="0" rightcolwidth="12" context="page"></cart-item>
          </div>
          <div style="margin-top: 10px">
            <button class="btn btn-info" type="button" v-on:click="displayOrder(order)"><i class="fa fa-fw fa-shopping-cart"></i> {{ _('Show more') }}</button>
          </div>
        </div>
      </div>
      <div class="row" v-if="customer">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-male"></i> {{ _('Customer') }}</h3>
          {{ customer.name }}<br />
          <template v-if="customer.email">{{ customer.email }}</template><br />
          {{ customer.address }}<br />
          {{ customer.zip }} {{ customer.city }}, {{ customer.country}}<br />
          <span v-if="customer.allowmarketing">{{ _('Marketing allowed') }}<br /></span>
          <span v-else>{{ _('Marketing NOT allowed') }}<br /></span>
          <template v-if="customer.phone">{{ _('Phone') }} {{ customer.phone }}<br /></template>
          <template v-if="customer.ident">{{ _('Personal ID Number') }} {{ customer.ident }}<br /></template>
          <button class="btn btn-info" type="button" v-on:click="displayCustomer(customer)"><i class="fa fa-fw fa-male"></i> {{ _('Show more') }}</button>
        </div>
      </div>
      <div class="row" v-if="product">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-cubes"></i> {{ _('Product') }}</h3>
          <h4>{{ product.name }}</h4>
          <product-popup :product="product" :shopId="product.shopid" :productShopId="product.shopid" :baseDomain="baseDomain" :locale="locale"></product-popup>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import components from '~/lib/components'
import products from '~/lib/products'
import { CartItem, ProductPopup } from '#components'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'

export default {
  data () {
    return {
      DateTime: DateTime,
      details: {
        ticket: null,
        show: null,
        valuecard: null,
        reservation: null,
        order: null,
        customer: null,
        product: null
      },
      code: ''
    }
  },
  mixins: [components, products],
  computed: {
    ...mapGetters(['locale', 'clientid']),
    shortLocale () {
      return this.locale.split('_')[0]
    },
    ticket () {
      if (this.details && this.details.ticket) {
        return this.details.ticket
      }
      return null
    },
    show () {
      if (this.details && this.details.show) {
        return this.details.show
      }
      return null
    },
    valuecard () {
      if (this.details && this.details.valuecard) {
        return this.details.valuecard
      }
      return null
    },
    reservation () {
      if (this.details && this.details.reservation) {
        return this.details.reservation
      }
      return null
    },
    order () {
      if (this.details && this.details.order) {
        return this.details.order
      }
      return null
    },
    customer () {
      if (this.details && this.details.customer) {
        return this.details.customer
      }
      return null
    },
    product () {
      if (this.details && this.details.product) {
        return this.details.product
      }
      return null
    },
    items () {
      if (this.order && this.order.ownItem) {
        return this.order.ownItem
      }
      return []
    },
    orderFullDate () {
      return DateTime.fromSeconds(Number(this.order.orderdate), { zone: this.timezone }).toFormat(this.dateFormat + ' ' + this.timeFormat)
    },
    isStandardValuecard () {
      if (!this.valuecard) {
        return false
      }
      if (parseInt(this.valuecard.singleuse) === 1 && parseFloat(this.valuecard.value) === 0 && parseInt(this.valuecard.count) > 0) {
        return false
      }
      return true
    },
    valuecardHasPayments () {
      if (!this.valuecard) {
        return false
      }
      if (this.valuecard.ownPayment && this.valuecard.ownPayment.length) {
        return true
      }
      if (this.valuecard.ownExternalpayment && this.valuecard.ownExternalpayment.length) {
        return true
      }
      return false
    },
    isValuecardInBaskets () {
      if (this.valuecard && this.valuecard.ownItem && this.valuecard.ownItem.length) {
        return true
      }
      return false
    }
  },
  methods: {
    setValidityDialog () {
      this.showDialog({
        title: this._('Set validity'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            handler: (data) => {
              this.setValidity(data.validto)
            }
          }
        ],
        fields: [
          {
            title: this._('Valid to'),
            type: 'date',
            name: 'validto',
            defaultValue: parseInt(this.valuecard.validto) ? DateTime.fromSeconds(Number(this.valuecard.validto), { zone: this.timezone }).toJSDate() : ''
          }
        ]
      })
    },
    async setValidity (validto) {
      let response
      const data = {
        id: this.valuecard.id, 
        validto: validto ? DateTime.fromJSDate(validto, { zone: this.timezone }).set({'hour': 23, 'minute': 59, 'second': 59}).toUnixInteger() : 0
      }
      try {
        response = await axios.patch(this.baseDomain + this.shopId + '/valuecards/' + this.valuecard.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.showError(e)
      }
      this.checkCode(this.valuecard.code)
    },
    async checkCode () {
      let response
      try {
        const code = this.code.toUpperCase().replace(/[^A-Z0-9]*/g, '')
        response = await axios.get(this.baseDomain + this.shopId + '/valuecards/' + code + '.json?details=true&apikey=' + this.apiKey)
      } catch(e) {}
      if (response) {
        this.showValuecardDetails(response.data)
      } else {
        this.details = {}
        this.showError(this._('The code you entered is invalid.'))
      }
    },
    async showTicketDetails (data) {
      let order = null
      let response
      if (data.shopId === this.shopId) {
        try {
          order = await this.loadOrder(data.shopId, data.ticket.order_id)
        } catch(e) {}
      }
      this.details = {
        ticket: data.ticket,
        show: data.show,
        order: order
      }
    },
    async showValuecardDetails (data) {
      let order = null
      let response
      if (data.shopId === this.shopId) {
        try {
          order = await this.loadOrder(data.shopId, data.order_id)
        } catch(e) {}
      }
      if (data && data.sharedShop && data.sharedShop[0].shopid !== this.shopId) {
        data.shopTitle = data.sharedShop[0].title
      }
      this.details = {
        valuecard: data,
        order: order
      }
    },
    async showCheckinDetails (data) {
      let order = null
      let response
      if (data.shopId === this.shopId) {
        try {
          order = await this.loadOrder(data.shopId, data.order_id)
        } catch(e) {}
      }
      this.details = {
        reservation: data.reservation,
        order: order
      }
    },
    async showCustomerDetails (data) {
      this.details = {
        customer: data.data
      }
    },
    async showProductDetails (data) {
      let product
      try {
        product = await this.loadProduct(data.shopId, data.id)
      } catch(e) {}
      product.shopid = data.shopId
      this.details = {
        product: product
      }
    },
    loadOrder (shopId, id) {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + shopId + '/orders/' + id + '.json?items=true&apikey=' + this.apiKey)
        } catch (e) {
          reject()
          return
        }
        resolve(response.data)
      })
    },
    loadProduct (shopId, id) {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + shopId + '/products/' + id + '.json?apikey=' + this.apiKey)
        } catch (e) {
          reject()
          return
        }
        resolve(response.data)
      })
    },
    getSeatDescription (seat) {
      if (seat.header || seat.title) {
        return (seat.header ? seat.header + ' / ' : '') + seat.title
      }
      return seat.name
    },
    displayCustomer (customer) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'CustomerDetails',
        title: this._('Customer') + ' ' + customer.id + (customer.name ? ': ' + customer.name : ''),
        width: 6,
        data: {
          customerId: customer.id
        }
      })
    },
    displayOrder (order) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'OrderDetails',
        title: this._('Order') + ' ' + order.id,
        width: 6,
        data: {
          orderId: order.id
        }
      })
    },
    displayInvoice (invoice) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'InvoiceDetails',
        title: this._('Transaction') + ' ' + invoice.id,
        width: 6,
        data: {
          invoiceId: invoice.id
        }
      })
    },
    displayReservation (reservation) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ReservationDetails',
        title: this._('Reservation') + ' ' + reservation.id,
        width: 6,
        data: {
          reservationId: reservation.id
        }
      })
    },
    deleteItemFromBasket (itemId, basketIdentifier) {
      this.showDialog({
        title: this._('Are you sure you want to delete this item from basket?'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            class: 'danger',
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Yes'),
            class: 'success',
            icon: 'check',
            handler: () => {
              this.handleDeleteItemFromBasket(itemId, basketIdentifier)
            }
          }
        ]
      })
    },
    async handleDeleteItemFromBasket (itemId, basketIdentifier) {
      try {
        if (basketIdentifier.length > 32) { // Pos basket, we need to pass the clientid
          await axios.delete(this.baseDomain + this.shopId + '/baskets/' + basketIdentifier + '/items/' + itemId + '.json?client=' + this.clientid)
        } else {
          await axios.delete(this.baseDomain + this.shopId + '/baskets/' + basketIdentifier + '/items/' + itemId + '.json')
        }
      } catch(e) {
        if (e?.response?.data?.errorMessage) {
          return this.showError(this._('Deleting the item failed!') + " " + e.response.data.errorMessage)
        }
        return this.showError(this._('Deleting the item failed!'))
      }
      this.checkCode()
      this.showNotification(this._('Item deleted from basket successfully!'))
    }
  },
  components: {
    CartItem,
    ProductPopup
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['check.checkinScanned'] = async (message) => {
        this.showCheckinDetails(message)
      }
      events['check.ticketScanned'] = async (message) => {
        this.showTicketDetails(message)
      }
      events['check.valuecardScanned'] = async (message) => {
        this.showValuecardDetails(message)
      }
      events['check.productScanned'] = async (message) => {
        this.showProductDetails(message)
      }
      events['check.setCustomer'] = async (message) => {
        this.showCustomerDetails(message)
      }
      this.events = events
    }
    this.enableEventBus()
  }
}

</script>

<style scoped>
.alert-deleted {
  width: 200px;
  margin-bottom: 10px;
}
.alert-deleted h4 {
  margin-bottom: 0px;
}
</style>